// RadioGroup — FE-302: Radio buttons with conditional_children per option
// E.g., "Holdback applies?" Yes → opens Holdback amount + Rationale fields
// When an option is selected and conditional_children[selectedValue] exists,
// recursively renders those children via SubtaskRenderer

import React, { useState } from "react";
import { RadioGroup as DSRadioGroup, RadioButton, Flex } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import * as styles from "./styles_vanilla.css";
import { useSubtaskActionsContext } from "./SubtaskActionsContext";
import { SubtaskRenderer } from "./index";
import type { SubtaskItem } from "../types";

interface RadioGroupProps {
  item: SubtaskItem;
  disabled?: boolean;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({ item, disabled = false }) => {
  // Derived from the prop so server-truth refetches and subtask rebinds
  // propagate instead of being silently ignored. Local state stays in the
  // render path for optimistic UI between click and refetch.
  const derivedValue = typeof item.value === "string" ? item.value : "";
  const [selectedValue, setSelectedValue] = useState<string>(derivedValue);
  // Track the last prop snapshot we synced from. When the subtask id or
  // the derived value changes, pull the new value into local state — React's
  // "adjusting state on prop change" pattern (runs during render, no effect).
  const [lastSeen, setLastSeen] = useState<{ id: SubtaskItem["id"]; value: string }>({
    id: item.id,
    value: derivedValue,
  });
  if (lastSeen.id !== item.id || lastSeen.value !== derivedValue) {
    setLastSeen({ id: item.id, value: derivedValue });
    setSelectedValue(derivedValue);
  }
  const { updateSubtaskValue } = useSubtaskActionsContext();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const next = e.target.value;
    setSelectedValue(next);
    void updateSubtaskValue(item.id, next, !!item.isCompleted);
  };

  const options = item.options || [];
  const conditionalChildren = item.conditional_children;
  const activeChildren =
    conditionalChildren && selectedValue ? conditionalChildren[selectedValue] || [] : [];

  return (
    <div className={styles.subtaskItem}>
      <DSRadioGroup
        name={`radio-${item.id}`}
        label={item.label}
        value={selectedValue}
        onChange={handleChange}
        helpText={item.helpText}
      >
        {options.map((option) => (
          <RadioButton
            key={option}
            value={option}
            name={`radio-${item.id}`}
            checked={selectedValue === option}
            disabled={disabled}
          >
            {option}
          </RadioButton>
        ))}
      </DSRadioGroup>

      {activeChildren.length > 0 && (
        <Flex flexDirection="column" gap={Sizes.MD} className={styles.radioConditionalChildren}>
          {activeChildren.map((child) => (
            <div key={child.id} className={styles.radioConditionalChild}>
              <SubtaskRenderer item={child} disabled={disabled} />
            </div>
          ))}
        </Flex>
      )}
    </div>
  );
};

export default RadioGroup;
